import React, { useCallback, useEffect, useState } from "react";
import { mixingAPI, posAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney, round2 } from "../pos/utils/money";
import { daysFromToday, formatDate } from "../utils/dates";
import Modal from "../components/Modal";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

const PAYMENT_METHODS = ["cash", "card", "mobile_payment", "credit"];

/** How many of the batch's own bottle_size_ml are still left to shelve normally. */
const remainingBottlesOf = (batch) =>
  Math.floor(
    (Number(batch.volume_ml) - Number(batch.volume_shelved_ml) + 0.0001) /
      Number(batch.bottle_size_ml),
  );

/**
 * Finds or picks the customer a credit sale goes on the tab of.
 *
 * Light-themed sibling of the register's own CustomerScan — no camera here,
 * the owner is at a desk, not scanning a card in someone's hand — but the
 * same card-code / phone lookup against the same customer directory.
 */
const CustomerPicker = ({ customer, onPicked, onCleared, currency }) => {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const lookup = async (value) => {
    if (!value) return;
    setBusy(true);
    setError("");

    try {
      const { data } = await posAPI.resolveCustomer(value.trim());
      onPicked(data);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? t("pos.customer.notRecognised")
          : t("pos.customer.checkFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const byPhone = async () => {
    setBusy(true);
    setError("");

    try {
      const { data } = await posAPI.findCustomer(phone);
      setMatches(data);
      if (data.length === 0) setError(t("pos.customer.nobody"));
    } catch (err) {
      setError(err.response?.data?.message || t("pos.customer.searchFailed"));
    } finally {
      setBusy(false);
    }
  };

  if (customer) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {customer.name}
          </p>
          {Number(customer.credit_balance) > 0 && (
            <p className="text-xs text-amber-700">
              {t("pos.customer.owes", {
                amount: formatMoney(customer.credit_balance, currency),
              })}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onCleared}
          className="shrink-0 text-xs font-semibold text-gray-500 hover:text-gray-700"
        >
          {t("common.remove")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          dir="ltr"
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          placeholder={t("pos.customer.codePlaceholder")}
          className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 uppercase tracking-widest text-sm"
        />
        <button
          type="button"
          onClick={() => lookup(code)}
          disabled={!code || busy}
          className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold disabled:opacity-50"
        >
          {t("pos.customer.find")}
        </button>
      </div>
      <div className="flex gap-2">
        <input
          type="tel"
          inputMode="numeric"
          dir="ltr"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder={t("pos.customer.phonePlaceholder")}
          className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={byPhone}
          disabled={phone.length < 4 || busy}
          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold disabled:opacity-50"
        >
          {t("pos.customer.searchButton")}
        </button>
      </div>
      {matches.length > 0 && (
        <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
          {matches.map((match) => (
            <li key={match.id}>
              <button
                type="button"
                onClick={() => onPicked(match)}
                className="w-full text-start px-3 py-2 hover:bg-gray-50 flex justify-between gap-2 text-sm"
              >
                <span>{match.name}</span>
                <span className="text-gray-400" dir="ltr">
                  {match.phone_hint}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

/**
 * Sells part of a still-steeping batch right now instead of waiting for the
 * whole thing to be shelved.
 *
 * Bottling is decided here, not carried over from the batch: the owner picks
 * which packaging material this quantity goes into (its size sets how many
 * bottles that is, and its stock is what actually decreases), and separately
 * whether that bottle/label cost counts toward the price. The owner still
 * thinks in how much liquid is going out — the bottle-equivalent and cost
 * price are shown as he types. Confirming records an actual sale: the
 * bottles blend into stock and sell straight back out in the same breath,
 * and what's left of the batch shrinks by exactly that much.
 */
const SellEarlyDialog = ({ batch, currency, onClose, onSold }) => {
  const { t } = useI18n();
  const remaining = round2(
    Number(batch.volume_ml) - Number(batch.volume_shelved_ml),
  );
  const materialsCostPerMl =
    Number(batch.volume_ml) > 0
      ? Number(batch.materials_cost) / Number(batch.volume_ml)
      : 0;

  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [packagingMaterialId, setPackagingMaterialId] = useState("");
  const [volumeMl, setVolumeMl] = useState("");
  const [includePackaging, setIncludePackaging] = useState(true);
  const [price, setPrice] = useState("");
  const [method, setMethod] = useState("cash");
  const [amountPaidUpfront, setAmountPaidUpfront] = useState("");
  const [customer, setCustomer] = useState(null);
  // A tab kept by name, when there is no customer card to scan
  const [tabName, setTabName] = useState("");
  const [tabSuggestions, setTabSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    mixingAPI
      .materials({ type: "packaging" })
      .then(({ data }) => {
        if (cancelled) return;

        const withSize = (data.materials || []).filter((m) => m.size_ml);
        setPackagingMaterials(withSize);
        if (withSize.length === 1) setPackagingMaterialId(withSize[0].id);
      })
      .finally(() => {
        if (!cancelled) setMaterialsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Existing tabs as the name is typed, so a regular's debts stay on one tab
  useEffect(() => {
    if (method !== "credit" || customer) return undefined;

    const timer = setTimeout(() => {
      posAPI
        .searchTabs(tabName.trim())
        .then(({ data }) => setTabSuggestions(data))
        .catch(() => setTabSuggestions([]));
    }, 200);

    return () => clearTimeout(timer);
  }, [method, customer, tabName]);

  const selectedMaterial = packagingMaterials.find(
    (material) => material.id === packagingMaterialId,
  );
  const bottleSize = selectedMaterial ? Number(selectedMaterial.size_ml) : 0;
  const sold = parseFloat(volumeMl) || 0;
  const bottles = bottleSize > 0 ? Math.round(sold / bottleSize) : 0;
  const juiceCost = round2(materialsCostPerMl * sold);
  const packagingCost =
    includePackaging && selectedMaterial
      ? round2(bottles * Number(selectedMaterial.unit_cost))
      : 0;
  const costOfSale = round2(juiceCost + packagingCost);
  const numericPrice = parseFloat(price) || 0;
  const isCredit = method === "credit";
  const amountPaidNow = isCredit
    ? parseFloat(amountPaidUpfront) || 0
    : numericPrice;
  const staysOnCredit = isCredit
    ? round2(Math.max(numericPrice - amountPaidNow, 0))
    : 0;

  const submit = async (event) => {
    event.preventDefault();

    if (sold <= 0 || sold > remaining) {
      setError(t("mixing.batches.sellEarly.tooMuch", { ml: remaining }));
      return;
    }

    if (!packagingMaterialId) {
      setError(t("mixing.batches.sellEarly.pickBottle"));
      return;
    }

    if (bottles < 1) {
      setError(t("mixing.batches.sellEarly.tooLittle"));
      return;
    }

    if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
      setError(t("mixing.batches.sellEarly.needsPrice"));
      return;
    }

    if (isCredit && !customer && !tabName.trim()) {
      setError(t("mixing.batches.sellEarly.creditNeedsTab"));
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await mixingAPI.sellEarly(batch.id, {
        volume_ml: sold,
        packaging_material_id: packagingMaterialId,
        include_packaging_cost: includePackaging,
        sale_price: numericPrice,
        payment_method: method,
        amount_paid: isCredit
          ? amountPaidUpfront === ""
            ? undefined
            : amountPaidNow
          : undefined,
        customer_id: isCredit ? customer?.id : undefined,
        // Found again by name if that tab already exists, opened otherwise
        credit_name: isCredit && !customer ? tabName.trim() : undefined,
      });
      onSold(t("mixing.batches.sellEarly.sold"));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("mixing.batches.sellEarly.sellFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("mixing.batches.sellEarly.title")}
      onClose={onClose}
      maxWidthClass="max-w-md"
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          {t("mixing.batches.sellEarly.warning")}
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("mixing.batches.sellEarly.volumeLabel")}
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              dir="ltr"
              value={volumeMl}
              onChange={(event) => setVolumeMl(event.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
            <button
              type="button"
              onClick={() => setVolumeMl(String(remaining))}
              className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              {t("mixing.batches.sellEarly.allRemaining")}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            <span dir="ltr">
              {t("mixing.batches.sellEarly.volumeHint", { ml: remaining })}
            </span>
            {sold > 0 && bottleSize > 0 && (
              <>
                {" · "}
                <span dir="ltr">
                  {t("mixing.batches.sellEarly.bottlesEquivalent", {
                    count: bottles,
                    size: bottleSize,
                  })}
                </span>
              </>
            )}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("mixing.batches.sellEarly.bottleLabel")}
          </label>
          {materialsLoading ? (
            <p className="text-sm text-gray-400">{t("common.loading")}</p>
          ) : packagingMaterials.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              {t("mixing.batches.sellEarly.noBottles")}
            </p>
          ) : (
            <select
              value={packagingMaterialId}
              onChange={(event) => setPackagingMaterialId(event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">{t("mixing.batches.sellEarly.pickBottle")}</option>
              {packagingMaterials.map((material) => (
                <option key={material.id} value={material.id}>
                  {t("mixing.batches.sellEarly.bottleOption", {
                    name: material.name,
                    size: material.size_ml,
                    stock: material.stock_quantity,
                  })}
                </option>
              ))}
            </select>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includePackaging}
            onChange={(event) => setIncludePackaging(event.target.checked)}
            className="rounded border-gray-300"
          />
          {t("mixing.batches.sellEarly.includePackaging")}
        </label>

        <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {t("mixing.batches.sellEarly.costPrice")}
          </span>
          <span className="font-semibold text-gray-900">
            {formatMoney(costOfSale, currency)}
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("mixing.batches.sellEarly.priceLabel")}
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            dir="ltr"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("mixing.batches.sellEarly.paymentMethod")}
          </label>
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {PAYMENT_METHODS.map((option) => (
              <option key={option} value={option}>
                {t(`common.payment.${option}`)}
              </option>
            ))}
          </select>
        </div>

        {isCredit && (
          <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("mixing.batches.sellEarly.customer")}
              </label>
              <CustomerPicker
                customer={customer}
                onPicked={setCustomer}
                onCleared={() => setCustomer(null)}
                currency={currency}
              />
            </div>

            {!customer && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("mixing.batches.sellEarly.tabName")}
                </label>
                <input
                  type="text"
                  list="early-sale-tabs"
                  autoComplete="off"
                  value={tabName}
                  onChange={(event) => setTabName(event.target.value)}
                  placeholder={t("pos.tab.placeholder")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <datalist id="early-sale-tabs">
                  {tabSuggestions.map((tab) => (
                    <option key={tab.id} value={tab.name} />
                  ))}
                </datalist>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("mixing.batches.sellEarly.paidUpfront")}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                dir="ltr"
                value={amountPaidUpfront}
                onChange={(event) => setAmountPaidUpfront(event.target.value)}
                placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {t("mixing.batches.sellEarly.staysOnCredit")}
              </span>
              <span className="font-semibold text-amber-700">
                {formatMoney(staysOnCredit, currency)}
              </span>
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-60"
          >
            {submitting
              ? t("mixing.batches.sellEarly.submitting")
              : t("mixing.batches.sellEarly.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Batches, and the steeping shelf.
 *
 * A batch's bottles only become sellable when the owner says the steep is
 * done, which is the whole point: juice sold too early tastes wrong and the
 * customer blames the shop, not the calendar.
 */
const Batches = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);
  const [batches, setBatches] = useState([]);
  const [ready, setReady] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(null);
  const [sellingEarly, setSellingEarly] = useState(null);

  const daysUntil = (date) => {
    const days = daysFromToday(date);
    if (days === null || days <= 0) return t("mixing.batches.readyNow");
    if (days === 1) return t("mixing.batches.readyTomorrow");
    return t("mixing.batches.daysToGo", { count: days });
  };

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        mixingAPI.batches({ limit: 50 }),
        mixingAPI.readyToShelve(),
      ]);
      setBatches(b.data);
      setReady(r.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("mixing.batches.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, fn, message) => {
    setBusy(id);
    setError("");

    try {
      await fn();
      setNotice(message);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("common.somethingWrong"));
    } finally {
      setBusy(null);
    }
  };

  const steeping = batches.filter((b) => b.status === "steeping");
  const done = batches.filter((b) => b.status !== "steeping");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {t("mixing.batches.title")}
        </h1>
        <p className="text-gray-600">{t("mixing.batches.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {ready.length > 0 && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-6">
          <h2 className="text-lg font-semibold text-emerald-900 mb-1">
            {t("mixing.batches.readyTitle", { count: ready.length })}
          </h2>
          <p className="text-sm text-emerald-800 mb-4">
            {t("mixing.batches.readyHint")}
          </p>
          <ul className="space-y-2">
            {ready.map((batch) => (
              <li
                key={batch.id}
                className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg px-4 py-3"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {batch.recipe_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {Number(batch.volume_shelved_ml) > 0
                      ? t("mixing.batches.leftOfBatch", {
                          left: remainingBottlesOf(batch),
                          total: batch.bottles,
                          size: batch.bottle_size_ml,
                        })
                      : `${batch.bottles} × ${batch.bottle_size_ml} ml`}
                  </p>
                </div>
                {can("mixing.edit") && (
                  <button
                    onClick={() =>
                      act(
                        batch.id,
                        () => mixingAPI.shelve(batch.id),
                        t("mixing.batches.bottlesAdded"),
                      )
                    }
                    disabled={busy === batch.id}
                    className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm disabled:opacity-60"
                  >
                    {busy === batch.id
                      ? t("mixing.batches.bottling")
                      : t("mixing.batches.putOut")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {steeping.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {t("mixing.batches.steeping")}
          </h2>
          <ul className="divide-y divide-gray-100">
            {steeping.map((batch) => (
              <li
                key={batch.id}
                className="py-3 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {batch.recipe_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {Number(batch.volume_shelved_ml) > 0
                      ? t("mixing.batches.leftOfBatch", {
                          left: remainingBottlesOf(batch),
                          total: batch.bottles,
                          size: batch.bottle_size_ml,
                        })
                      : `${batch.bottles} × ${batch.bottle_size_ml} ml`}
                    {" · "}
                    {t("mixing.batches.perBottle", {
                      amount: money(batch.computed_unit_cost),
                    })}{" "}
                    · {daysUntil(batch.ready_on)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {can("mixing.edit") && (
                    <button
                      onClick={() => {
                        if (!batch.product_id) {
                          setError(t("mixing.batches.sellEarly.noProduct"));
                          return;
                        }
                        setError("");
                        setSellingEarly(batch);
                      }}
                      disabled={busy === batch.id}
                      className="text-sm text-amber-700 hover:underline"
                    >
                      {t("mixing.batches.shelveEarly")}
                    </button>
                  )}
                  {can("mixing.delete") && (
                    <button
                      onClick={() =>
                        act(
                          batch.id,
                          () =>
                            mixingAPI.discard(
                              batch.id,
                              t("mixing.batches.discardReason"),
                            ),
                          t("mixing.batches.discarded"),
                        )
                      }
                      disabled={busy === batch.id}
                      className="text-sm text-gray-400 hover:text-red-600"
                    >
                      {t("mixing.batches.discard")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {t("mixing.batches.history")}
        </h2>
        {done.length === 0 ? (
          <EmptyState screen="batches" message={t("mixing.batches.nothingMixed")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {done.map((batch) => (
              <li key={batch.id} className="py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium text-gray-900">
                    {batch.recipe_name}
                    <span
                      className={`ms-2 text-xs px-2 py-0.5 rounded-full ${
                        batch.status === "discarded"
                          ? "bg-red-50 text-red-700"
                          : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {batch.status === "discarded"
                        ? t("mixing.batches.status.discarded")
                        : t("mixing.batches.status.bottled")}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    {money(batch.total_cost)} ·{" "}
                    {t("mixing.batches.perBottle", {
                      amount: money(batch.computed_unit_cost),
                    })}
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  {formatDate(batch.produced_on)} ·{" "}
                  <span dir="ltr">
                    {t("mixing.batches.produced", {
                      volume: batch.volume_ml,
                      count: batch.bottles,
                      size: batch.bottle_size_ml,
                    })}
                  </span>
                  {Number(batch.remainder_ml) > 0 &&
                    t("mixing.batches.over", { ml: batch.remainder_ml })}
                  {Number(batch.nicotine_mg) > 0 && (
                    <span dir="ltr">
                      {" · "}
                      {t("mixing.batches.strength", {
                        mg: batch.nicotine_mg,
                      })}
                    </span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {sellingEarly && (
        <SellEarlyDialog
          batch={sellingEarly}
          currency={currency}
          onClose={() => setSellingEarly(null)}
          onSold={(message) => {
            setSellingEarly(null);
            setNotice(message);
            setError("");
            load();
          }}
        />
      )}
    </div>
  );
};

export default Batches;
